import Tile from './tile'
import TileDrawer from './tile_drawer'

/**
 * Setの差分
 */
const diff = (a, b) => {
  return [...a].filter(x => !b.has(x))
}

/**
 * Setの共通部分
 */
const intersection = (a, b) => {
  return [...a].filter(x => b.has(x))
}

/**
 * NOTE:
 * dynamicにgoogle.maps.OverlayViewをextendするために
 * class構文をfunctionでラップしている
 *
 * @see https://www.mikedoesweb.com/2017/dynamic-super-classes-extends-in-es6/
 */
/* global google */
export const OverlayView = (SuperClass, ...props) => {
  class OverlayView extends SuperClass {
    constructor(callbackEvents) {
      super()
      this.callbackEvents = callbackEvents || {}
    }

    // 登録したイベントハンドラを解除時のために保持
    listeners = []

    // 地図描画中のタイル
    currentTiles = new Set()

    // 初期表示のピクセル座標を保持
    // drag時に座標差分を算出するため
    initialCenterPixel = { left: 0, top: 0 }

    /**
     * 地図のzoomレベルを変更中かどうか判定
     * 返却値が1のときはzoomレベル変更完了
     */
    zoomProgressRate(zoom) {
      const map = this.getMap()
      const projection = this.getProjection()
      if (!(map && projection)) { return }
      zoom = zoom || map.getZoom()
      return projection.getWorldWidth() / (Tile.SIZE * Math.pow(2, zoom))
    }

    /**
     * OverlayViewの描画
     * @see https://developers.google.com/maps/documentation/javascript/examples/overlay-simple
     */
    draw() {
      const projection = this.getProjection()
      const scale = this.zoomProgressRate()
      const { x: left, y: top } = projection.fromLatLngToDivPixel(this.initialCenter)
      const containers = [this.container, this.mouseTargetContainer]

      containers.forEach(container => {
        container.style.willChange = "transform, top, left"
        container.style.top = `${top}px`
        container.style.left = `${left}px`
        container.style.transform = `scale(${scale},${scale})`
      })
      this.initialCenterPixel = { left, top }
    }

    /**
     * OverlayViewが地図に追加されたときのイベントハンドラ
     * @see https://developers.google.com/maps/documentation/javascript/examples/overlay-simple
     */
    onAdd() {
      this.subscribeEvents()
      this.attachContainers()
    }

    /**
     * OverlayViewが地図から削除されたときのイベントハンドラ
     * @see https://developers.google.com/maps/documentation/javascript/examples/overlay-simple
     */
    onRemove() {
      this.unsubscribeEvents()
      this.discardAllTiles()
      this.detachContainers()
    }

    /**
     * 地図のタイル読込みイベントハンドラ
     */
    onTilesLoaded = () => {
      if (this.zoomProgressRate() != 1) { return }
      const bounds = this.getMap().getBounds()
      const tiles = Tile.tilesCoveringBounds(bounds, this.getMap().getZoom())
      const discardingTiles = diff(this.currentTiles, new Set(tiles))
      const appendingTiles = diff(tiles, this.currentTiles)
      discardingTiles.forEach(tile => this.detach(tile))
      appendingTiles.forEach(tile => this.attach(tile))
      this.trigger('tiles_appended', intersection(appendingTiles, this.currentTiles))
    }

    /**
     * zoomレベル変更イベントハンドラ
     */
    onZoomChanged = () => {
      this.discardAllTiles()
      this.removePointerDiv()
    }

    /**
     * タイルの描画を更新
     */
    reloadTiles() {
      this.discardAllTiles()
      this.onTilesLoaded()
    }

    /**
     * タイルに対応するroomGroups読込みイベントハンドラ
     * TODO: handle error
     */
    onRoomGroupsLoaded = ({ tile, roomGroups, error }) => {
      // tile.drawerが無い => 画面から外れているので描画しない
      if (!tile.drawer) { return }

      tile.drawer.clear()
      roomGroups.forEach(roomGroup => {
        tile.drawer.plot(this.getPlotDimensionFor(tile, roomGroup), roomGroup)
      })
      tile.drawer.draw()
    }

    /**
     * mousemoveイベントハンドラ
     */
    onMouseMove = ({ latLng }) => {
      const roomGroup = this.getRoomGroupFromLatLng(latLng)
      if (roomGroup) {
        this.trigger('room_group_hovered', roomGroup)
        this.setDimensionToHoveringMarker(roomGroup)
      } else {
        this.removePointerDiv()
      }
    }

    /**
     * clickイベントハンドラ
     */
    onClick = ({ latLng }) => {
      const roomGroup = this.getRoomGroupFromLatLng(latLng)
      this.trigger('room_group_clicked', roomGroup)
    }

    /**
     * イベント発行
     */
    trigger(event, payload) {
      const map = this.getMap()
      google.maps.event.trigger(map, event, payload)
    }

    /**
     * イベント購読
     */
    subscribeEvents() {
      const { addListener } = google.maps.event
      const map = this.getMap()

      this.listeners = Object.entries({
        idle: this.onTilesLoaded,
        tilesloaded: this.onTilesLoaded,
        zoom_changed: this.onZoomChanged,
        room_groups_loaded: this.onRoomGroupsLoaded,
        mousemove: this.onMouseMove,
        click: this.onClick,
        ...this.callbackEvents
      }).map(([event, handler]) => addListener(map, event, handler))

      this.onTilesLoaded()
    }

    /**
     * イベント購読解除
     */
    unsubscribeEvents() {
      const { removeListener } = google.maps.event
      this.listeners.forEach(removeListener)
    }

    /**
     * 描画中のタイルのDOMを保持するコンテナdivを生成
     */
    attachContainers() {
      this.container = this.createContainerDiv()
      this.getPanes().overlayLayer.appendChild(this.container)
      this.mouseTargetContainer = this.createContainerDiv()
      this.getPanes().overlayMouseTarget.appendChild(this.mouseTargetContainer)
      this.initialCenter = this.getMap().getCenter()
    }

    /**
     * 描画中タイルのDOMを保持するコンテナdivを破棄
     */
    detachContainers() {
      this.container.parentElement && this.container.parentElement.removeChild(this.container)
      this.mouseTargetContainer.parentElement && this.mouseTargetContainer.parentElement.removeChild(this.mouseTargetContainer)
      delete this.container
      delete this.mouseTargetContainer
    }

    /**
     * コンテナdivを生成して返す
     */
    createContainerDiv() {
      const div = document.createElement('div')
      div.style.position = "absolute"
      div.style.top = "0"
      div.style.left = "0"
      return div
    }

    /**
     * 描画中の全タイルを破棄
     */
    discardAllTiles() {
      this.currentTiles.forEach(tile => this.detach(tile))
    }

    /**
     * タイルオブジェクト描画用の<canvas>をDOMツリーに追加
     */
    attach(tile) {
      if (!this.container) { return this.detach(tile) }
      tile.drawer = tile.drawer || new TileDrawer()

      if (!tile.drawer.isContainedTo(this.container)) {
        tile.drawer.setDimension(this.getTileDimensionFor(tile))
        tile.drawer.appendTo(this.container)
      }

      this.currentTiles.add(tile)
    }

    /**
     * タイルオブジェクト描画用の<canvas>をDOMツリーから外す
     */
    detach(tile) {
      tile.drawer && tile.drawer.remove()
      delete tile.drawer
      this.currentTiles.delete(tile)
    }

    /**
     * タイルオブジェクトを描画するピクセル座標を算出
     */
    getTileDimensionFor(tile) {
      const projection = this.getProjection()
      const { southWest, northEast } = tile
      const { x: left, y: bottom } = projection.fromLatLngToDivPixel(southWest)
      const { x: right, y: top } = projection.fromLatLngToDivPixel(northEast)
      const width = right - left
      const height = bottom - top
      const { left: diffX, top: diffY } = this.initialCenterPixel
      return { left: left - diffX, top: top - diffY, width, height }
    }

    /**
     * roomGroupを描画するタイルの左上からの相対ピクセル座標を返す
     */
    getPlotDimensionFor(tile, roomGroup) {
      const projection = this.getProjection()
      const { lat, lon: lng } = roomGroup.property
      const latLng = new google.maps.LatLng({ lat: lat - 0, lng: lng - 0 })
      const { x: left, y: top } = projection.fromLatLngToDivPixel(latLng)
      const { x: tileLeft, y: tileTop } = projection.fromLatLngToDivPixel(tile.latLng)
      const localLeft = left - tileLeft
      const localTop = top - tileTop
      return { left: localLeft, top: localTop }
    }

    /**
     * mousemove,click時のlatLngから、該当するタイル上の相対ピクセル座標を返す
     */
    getHoverDimensionFor(tile, latLng) {
      const projection = this.getProjection()
      const { x: left, y: top } = projection.fromLatLngToDivPixel(latLng)
      const { x: tileLeft, y: tileTop } = projection.fromLatLngToDivPixel(tile.latLng)
      const localLeft = left - tileLeft
      const localTop = top - tileTop
      return { left: localLeft, top: localTop }
    }

    /**
     * hover中のマーカーに対応するdiv要素
     */
    get pointerDiv() {
      if (this._hoveredDiv) { return this._hoveredDiv }
      const div = document.createElement('div')
      div.style.position = 'absolute'
      div.style.width = `${TileDrawer.MARKER_WIDTH}px`
      div.style.height = `${TileDrawer.MARKER_HEIGHT}px`
      div.style.backgroundImage = `url('/assets/image/search/map/marker-fill.svg')`
      div.style.cursor = 'pointer'
      div.style.willChange = "top, left"
      return (this._hoveredDiv = div)
    }

    /**
     * pointerDivをDOMツリーから削除
     */
    removePointerDiv() {
      this.lastHoveredRoomGroup = null
      this.pointerDiv.parentElement && this.pointerDiv.parentElement.removeChild(this.pointerDiv)
    }

    /**
     * hover中のマーカーに対応するdiv要素の位置を更新
     */
    setDimensionToHoveringMarker(roomGroup) {
      if (this.lastHoveredRoomGroup == roomGroup) { return }
      const projection = this.getProjection()
      const { lat, lon: lng } = roomGroup.property
      const latLng = new google.maps.LatLng({ lat: lat - 0, lng: lng - 0 })
      const { x, y } = projection.fromLatLngToDivPixel(latLng)
      const { left: diffX, top: diffY } = this.initialCenterPixel
      this.pointerDiv.style.left = `${Math.round(x - diffX - (TileDrawer.MARKER_WIDTH / 2))}px`
      this.pointerDiv.style.top = `${Math.round(y - diffY - (TileDrawer.MARKER_HEIGHT))}px`
      this.lastHoveredRoomGroup = roomGroup
      if (!this.pointerDiv.parentElement) {
        this.mouseTargetContainer.appendChild(this.pointerDiv)
      }
    }

    /**
     * マウスカーソル下のマーカーに対応するroomGroupを返す
     */
    getRoomGroupFromLatLng(latLng) {
      const zoom = this.getMap().getZoom()
      const tile = Tile.at(latLng, zoom)

      const candidate = this.getAdjacentTilesOf(tile).reduce((roomGroups, tile) => {
        if (!tile.drawer) { return roomGroups }
        const localCoords = this.getHoverDimensionFor(tile, latLng)
        return roomGroups.concat(tile.drawer.hits(localCoords))
      }, []).reduce((prev, current) => {
        if (!prev) { return current }
        if (!current) { return prev }
        const [ prevDistance ] = prev
        const [ currentDistance ] = current
        return prevDistance <= currentDistance ? prev : current
      }, null)

      if (candidate) {
        const [, roomGroup] = candidate
        return roomGroup
      }
    }

    /**
     * 指定したタイルに隣接するタイル配列を返す
     */
    getAdjacentTilesOf(tile) {
      return [...this.currentTiles].filter(subject => (
        tile.z == subject.z &&
        tile.x - 1 <= subject.x && subject.x <= tile.x + 1 &&
        tile.y - 1 <= subject.y && subject.y <= tile.y + 1
      ))
    }
  }

  return new OverlayView(...props)
}
